import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FiArrowUpRight, FiUser, FiCalendar, FiHome, FiChevronRight } from "react-icons/fi";
import { BLOG_POSTS } from "../data/blogData";
import { openConsultationMail } from "../config/contact";

const POPULAR_TAGS = ["SEO", "Digital Marketing", "PPC", "Keyword Research", "Content", "Email Marketing", "Social Media"];

function slugify(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function renderContent(content) {
  const lines = content.trim().split("\n");
  const elements = [];
  let key = 0;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    if (line.startsWith("## ")) {
      const heading = line.slice(3);
      const id = slugify(heading);
      elements.push(<h2 key={key++} id={id} className="blog-detail-h2">{heading}</h2>);
    } else if (line.startsWith("**") && line.endsWith("**")) {
      elements.push(<p key={key++} className="blog-detail-bold">{line.slice(2, -2)}</p>);
    } else if (/^\*\*[^*]+:\*\*/.test(line)) {
      const colonIdx = line.indexOf(":**");
      const boldPart = line.slice(2, colonIdx);
      const rest = line.slice(colonIdx + 3);
      elements.push(<p key={key++} className="blog-detail-p"><strong>{boldPart}:</strong>{rest}</p>);
    } else {
      elements.push(<p key={key++} className="blog-detail-p">{line}</p>);
    }
  }
  return elements;
}

function LeaveReply() {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = (e) => { e.preventDefault(); setSubmitted(true); };

  return (
    <div className="blog-reply-section">
      <h3 className="blog-reply-title">Leave a Reply</h3>
      <p className="blog-reply-sub">Provide clear contact information, including phone number, email, and address.</p>
      {submitted ? (
        <div className="blog-reply-success"><span>✓</span><p>Thank you! We'll review and respond shortly.</p></div>
      ) : (
        <form className="blog-reply-form" onSubmit={handleSubmit}>
          <div className="blog-reply-row">
            <input name="firstName" type="text" placeholder="First Name" value={form.firstName} onChange={handleChange} required />
            <input name="lastName" type="text" placeholder="Last Name" value={form.lastName} onChange={handleChange} required />
          </div>
          <div className="blog-reply-row">
            <input name="email" type="email" placeholder="Email" value={form.email} onChange={handleChange} required />
            <input name="phone" type="tel" placeholder="Phone" value={form.phone} onChange={handleChange} />
          </div>
          <input name="subject" type="text" placeholder="Subject" value={form.subject} onChange={handleChange} />
          <textarea name="message" rows={5} placeholder="Message" value={form.message} onChange={handleChange} required />
          <button type="submit" className="blog-reply-btn">Submit <FiArrowUpRight /></button>
        </form>
      )}
    </div>
  );
}

function BlogDetailPage() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.id === slug);
  if (!post) return <Navigate to="/blog" replace />;
  const related = BLOG_POSTS.filter((p) => p.id !== post.id && p.category === post.category).slice(0, 2);

  // Extract headings for TOC
  const tocItems = post.content.split("\n")
    .filter(l => l.trim().startsWith("## "))
    .map(l => {
      const text = l.trim().slice(3);
      return { text, id: slugify(text) };
    });

  const scrollToHeading = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <main className="blog-detail-page">
      {/* NO HERO — starts with breadcrumb + content */}
      <section className="blog-detail-section">
        <div className="container-custom">

          {/* Breadcrumb */}
          <nav className="blog-detail-breadcrumb" aria-label="breadcrumb">
            <Link to="/"><FiHome /> Home</Link>
            <FiChevronRight />
            <Link to="/blog">Our Blog</Link>
            <FiChevronRight />
            <span>{post.category}</span>
          </nav>

          <div className="blog-detail-layout">

            {/* MAIN CONTENT */}
            <motion.article
              className="blog-detail-article"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="blog-detail-meta-top">
                <span className="blog-detail-cat-pill">{post.category}</span>
                <span><FiUser /> {post.author}</span>
                <span><FiCalendar /> {post.date}</span>
              </div>

              <h1 className="blog-detail-title">{post.title}</h1>

              <div className="blog-detail-hero-img">
                <img src={post.image} alt={post.title} />
              </div>

              <div className="blog-detail-content">
                {renderContent(post.content)}
              </div>

              <div className="blog-detail-tags">
                <span className="blog-detail-tag">{post.category}</span>
                <span className="blog-detail-tag">Digital Marketing</span>
                <span className="blog-detail-tag">Delhi NCR</span>
              </div>

              {related.length > 0 && (
                <div className="blog-related">
                  <h3>Related Posts</h3>
                  <div className="blog-related-grid">
                    {related.map((r) => (
                      <Link key={r.id} to={`/blog/${r.id}`} className="blog-related-card">
                        <img src={r.image} alt={r.title} />
                        <div>
                          <span><FiCalendar /> {r.date}</span>
                          <p>{r.title}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <LeaveReply />
            </motion.article>

            {/* SIDEBAR */}
            <aside className="blog-sidebar">
              {/* TOC — functional scroll */}
              {tocItems.length > 0 && (
                <div className="blog-sidebar-widget">
                  <h4>Table of Contents</h4>
                  <ul className="blog-toc-list">
                    {tocItems.map((item) => (
                      <li key={item.id}>
                        <button onClick={() => scrollToHeading(item.id)}>{item.text}</button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="blog-sidebar-widget">
                <h4>Most Recent Posts</h4>
                <ul className="blog-recent-list">
                  {BLOG_POSTS.filter((p) => p.id !== post.id).slice(0, 5).map((p) => (
                    <li key={p.id}>
                      <Link to={`/blog/${p.id}`}>
                        <img src={p.image} alt={p.title} />
                        <div>
                          <span><FiCalendar /> {p.date}</span>
                          <p>{p.title}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="blog-sidebar-widget">
                <h4>Popular Tags</h4>
                <div className="blog-popular-tags">
                  {POPULAR_TAGS.map((tag) => (
                    <Link key={tag} to="/blog" className="blog-popular-tag">{tag}</Link>
                  ))}
                </div>
              </div>

              <div className="blog-sidebar-widget blog-help-widget">
                <h4>If You Need Any Help Contact With Us</h4>
                <button onClick={openConsultationMail} className="blog-help-phone">
                  Get Free Consultation
                  <span className="blog-help-arrow"><FiArrowUpRight /></span>
                </button>
              </div>
            </aside>

          </div>
        </div>
      </section>
    </main>
  );
}

export default BlogDetailPage;
